import * as React from 'react'
import clsx from 'clsx'
import { StepConnector } from '../StepConnector'
import { SxProps, sx as sxFunc } from '../sx'
import StepperContext, { dashStyleType } from './StepperContext'
import style from './index.module.scss'

export interface StepperClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element if `orientation="horizontal"`. */
  horizontal: string
  /** Styles applied to the root element if `orientation="vertical"`. */
  vertical: string
  /** Styles applied to the root element if `alternativeLabel={true}`. */
  alternativeLabel: string
}

export type Orientation = 'horizontal' | 'vertical'

export interface StepperProps
  extends Partial<React.HTMLAttributes<HTMLDivElement>> {
  /**
   * Set the active step (zero based index).
   * Set to -1 to disable all the steps.
   * @default 0
   */
  activeStep?: number
  /**
   * If set to 'true' and orientation is horizontal,
   * then the step label will be positioned under the icon.
   * @default false
   */
  alternativeLabel?: boolean
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   * @default div
   */
  Component?: React.ElementType
  /**
   * Two or more `<Step />` components.
   */
  children?: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<StepperClasses>
  /**
   * An element to be placed between each step.
   * @default <StepConnector />
   */
  connector?: React.ReactElement<any, any> | null
  /**
   * If set the `Stepper` will not assist in controlling steps for linear flow.
   * @default false
   */
  nonLinear?: boolean
  /**
   * The component orientation (layout flow direction).
   * @default 'horizontal'
   */
  orientation?: Orientation
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * The variant to use.
   * @default 'standard'
   */
  variant?: 'standard' | 'dash'
  /**
   * Dash style is apply if varisnt is 'dash', Styles will allow to set width and height of the dash.
   */
  dashStyle?: dashStyleType
}

const defaultConnector = <StepConnector />

export const Stepper = React.forwardRef<HTMLDivElement, StepperProps>(
  (props, ref) => {
    const {
      activeStep = 0,
      alternativeLabel = false,
      classes,
      children,
      className,
      Component = 'div',
      connector = defaultConnector,
      nonLinear = false,
      orientation = 'horizontal',
      variant = 'standard',
      dashStyle = { width: '64px', height: '4px', gap: '4px' },
      sx,
      ...other
    } = props

    const childrenArray = React.Children.toArray(children).filter(Boolean)
    const steps = childrenArray.map((step, index) => {
      // @ts-ignore
      return React.cloneElement(step, {
        index,
        last: index + 1 === childrenArray.length,
      })
    })
    const contextValue = React.useMemo(
      () => ({
        activeStep,
        alternativeLabel,
        connector,
        nonLinear,
        orientation,
        variant,
        dashStyle,
      }),
      [
        activeStep,
        alternativeLabel,
        connector,
        nonLinear,
        orientation,
        variant,
        dashStyle,
      ]
    )
    return (
      <StepperContext.Provider value={contextValue}>
        <Component
          className={clsx(
            classes?.root,
            className,
            style.root,
            style?.[`orientation-${orientation}`],
            classes?.[orientation],
            alternativeLabel && [
              style.alternativeLabel,
              classes?.alternativeLabel,
            ],
            sxFunc(sx)
          )}
          ref={ref}
          {...other}
        >
          {steps}
        </Component>
      </StepperContext.Provider>
    )
  }
)

Stepper.displayName = 'Stepper'
